import React from 'react';
import { ScrollMenu, VisibilityContext } from 'react-horizontal-scrolling-menu';
import './hideScrollbar.css';
import { ICategorias } from '../../Types';
import { GlobalContext } from '../../contexts/HomeContext';
import { Shimmer } from '../Shimmer';
import useDrag from './UseDrag';
import { Card } from './Card';
import { LeftArrow, RightArrow } from './Arrows';

// prettier-ignore
type scrollVisibilityApiType = React.ContextType<typeof VisibilityContext>;

function MenuCategorias() {
  const { dadosApp } = React.useContext(GlobalContext);
  const [selected, setSelected] = React.useState<string>('');
  const apiRef = React.useRef<scrollVisibilityApiType>(null!);

  const { dragStart, dragStop, dragMove, dragging } = useDrag();

  const getCategoriaId = React.useCallback((item: ICategorias) => {
    const categoria = item.categoria_ecommerce === '' ? item.subgrupo : item.categoria_ecommerce;
    return categoria.toUpperCase().trim();
  }, []);

  // prettier-ignore
  const handleDrag = ({ scrollContainer }: scrollVisibilityApiType) => (ev: React.MouseEvent) => dragMove(ev, (posDiff: number) => {
    if (scrollContainer.current) {
      scrollContainer.current.scrollLeft += posDiff;
    }
  });

  // NOTE: for select item
  const handleItemClick = (item: ICategorias) => () => scrollToTargetAdjusted(item);

  const scrollToTargetAdjusted = (item: ICategorias) => {
    const categoria = getCategoriaId(item);

    setSelected(categoria);
    let element = document.getElementById(categoria);

    let top = 150;
    let headerOffset = window.scrollY - top;
    let elementPosition = element?.getBoundingClientRect().top;
    if (elementPosition !== undefined) {
      let offsetPosition = elementPosition + headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  // Faz o scroll do menu para manter a categoria ativa visível
  const scrollMenuToActiveCategory = React.useCallback((activeCategoryId: string) => {
    if (!apiRef.current) return;

    const { scrollToItem, getItemById } = apiRef.current;
    const foundItem = getItemById(activeCategoryId);

    if (foundItem) {
      scrollToItem(foundItem, 'smooth', 'center');
    }
  }, []);

  const handleScroll = React.useCallback(() => {
    let scrollDistance = document.body.scrollTop || document.documentElement.scrollTop;
    scrollDistance = scrollDistance - 250;
    let activeCategoryId = '';

    document.querySelectorAll('.page-section').forEach(function (i) {
      var el = i as HTMLElement;
      if (el.offsetTop <= scrollDistance) {
        activeCategoryId = i.id.trim();
        setSelected(activeCategoryId);
      }
    });

    // Faz scroll automático do menu quando uma nova categoria fica ativa
    if (activeCategoryId) {
      scrollMenuToActiveCategory(activeCategoryId);
    }
  }, [scrollMenuToActiveCategory]);

  React.useEffect(() => {
    dadosApp.Cardapio.Categorias.slice(0, 1).map((i: ICategorias) => {
      setSelected(getCategoriaId(i));
    });

    document.addEventListener('scroll', handleScroll);
    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, [dadosApp.Cardapio.Categorias, getCategoriaId, handleScroll]);

  return (
    <>
      <div id="categorias" className="categorias stickyMenu categorias_padZero">
        <div className="container categorias_padZero">
          <div className="scrollMenu">
            {dadosApp.Cardapio.Categorias.length === 0 && <Shimmer type="Categorias" />}

            {dadosApp.Cardapio.Categorias.length > 0 && (
              <ScrollMenu
                LeftArrow={LeftArrow}
                RightArrow={RightArrow}
                onWheel={onWheel}
                apiRef={apiRef}
                options={{
                  ratio: 0.9,
                  rootMargin: '5px',
                  threshold: [0.01, 0.05, 0.5, 0.75, 0.95, 1],
                  throttle: 0,
                }}
                onMouseDown={() => dragStart}
                onMouseUp={() => dragStop}
                onMouseMove={handleDrag}
              >
                {dadosApp.Cardapio.Categorias.map((item: ICategorias) => {
                  const categoriaName = item.categoria_ecommerce === '' ? item.subgrupo : item.categoria_ecommerce;
                  const categoriaId = getCategoriaId(item);
                  return (
                    <Card
                      title={categoriaName}
                      itemId={categoriaId}
                      key={String(item.id)}
                      onClick={handleItemClick(item)}
                      selected={categoriaId === selected}
                      disabled={dragging}
                    />
                  );
                })}
              </ScrollMenu>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default MenuCategorias;

function onWheel(apiObj: scrollVisibilityApiType, ev: React.WheelEvent): void {
  const isThouchpad = Math.abs(ev.deltaX) !== 0 || Math.abs(ev.deltaY) < 15;

  if (isThouchpad) {
    ev.stopPropagation();
    return;
  }

  if (ev.deltaY < 0) {
    apiObj.scrollNext();
  } else if (ev.deltaY > 0) {
    apiObj.scrollPrev();
  }
}
